import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Settings, Shield, Brain, Users, Save, RefreshCw, Radar, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'global', label: 'Global Settings', icon: Settings },
  { key: 'users', label: 'User Limits', icon: Users },
  { key: 'llm', label: 'LLM Config', icon: Brain },
  { key: 'safety', label: 'Safety', icon: Shield },
  { key: 'scraping', label: 'Scraping Jobs', icon: Radar },
];

export default function ProspectingAdmin() {
  const { isDark } = useTheme();
  const [tab, setTab] = useState('global');
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState({ prospecting_users: [], crm_users: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); fetchUsers(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/admin/settings`, { credentials: 'include' });
      if (res.ok) setSettings(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/admin/users`, { credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (err) { console.error(err); }
  };

  const saveSettings = async (partial) => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/prospecting/admin/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(partial),
      });
      await fetchSettings();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border`;
  const labelCls = `block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  const Tip = ({ text }) => (
    <span className="relative group inline-block ml-1 cursor-help">
      <Info className={`w-3.5 h-3.5 inline ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      <span className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg text-xs w-56 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'bg-[#222] text-gray-200 border border-white/10' : 'bg-gray-900 text-white'} shadow-lg`}>{text}</span>
    </span>
  );

  const NumberField = ({ label, value, onChange, tip }) => (
    <div>
      <label className={labelCls}>{label}{tip && <Tip text={tip} />}</label>
      <input type="number" value={value || ''} onChange={e => onChange(parseInt(e.target.value) || 0)} className={input} />
    </div>
  );

  if (loading || !settings) {
    return (
      <AppLayout title="Prospecting Admin">
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Prospecting Admin">
      <div className="space-y-6" data-testid="prospecting-admin">
        <ProspectingNav />
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Prospecting Configuration</h1>

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${tab === t.key ? 'bg-[#A2FF00] text-black font-medium' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Global Settings */}
        {tab === 'global' && (
          <div className="space-y-6">
            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Company-Wide Daily Limits</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <NumberField label="Total Actions" tip="Maximum automated actions (views, messages, connections) across ALL users per day. Recommended: 300-500." value={settings.company_limits?.max_daily_actions} onChange={v => setSettings(s => ({ ...s, company_limits: { ...s.company_limits, max_daily_actions: v } }))} />
                <NumberField label="Scraping Requests" tip="Maximum follower scraping requests across all users per day." value={settings.company_limits?.max_daily_scraping_requests} onChange={v => setSettings(s => ({ ...s, company_limits: { ...s.company_limits, max_daily_scraping_requests: v } }))} />
                <NumberField label="Connection Requests" tip="Total LinkedIn connection requests across all users per day. LinkedIn safe limit is ~100/day company-wide." value={settings.company_limits?.max_daily_connection_requests} onChange={v => setSettings(s => ({ ...s, company_limits: { ...s.company_limits, max_daily_connection_requests: v } }))} />
                <NumberField label="Messages" tip="Total LinkedIn direct messages across all users per day." value={settings.company_limits?.max_daily_messages} onChange={v => setSettings(s => ({ ...s, company_limits: { ...s.company_limits, max_daily_messages: v } }))} />
                <NumberField label="Emails" tip="Total outreach emails across all users per day." value={settings.company_limits?.max_daily_emails} onChange={v => setSettings(s => ({ ...s, company_limits: { ...s.company_limits, max_daily_emails: v } }))} />
              </div>
            </div>

            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Default Per-User Limits</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <NumberField label="Profile Views/Day" tip="Max profile views per user per day. LinkedIn safe limit is ~50-80/day." value={settings.default_user_limits?.max_daily_profile_views} onChange={v => setSettings(s => ({ ...s, default_user_limits: { ...s.default_user_limits, max_daily_profile_views: v } }))} />
                <NumberField label="Connection Requests/Day" tip="Max connection requests per user. LinkedIn safe limit is ~20-25/day. Start lower during ramp-up." value={settings.default_user_limits?.max_daily_connection_requests} onChange={v => setSettings(s => ({ ...s, default_user_limits: { ...s.default_user_limits, max_daily_connection_requests: v } }))} />
                <NumberField label="Messages/Day" tip="Max LinkedIn direct messages per user per day." value={settings.default_user_limits?.max_daily_messages} onChange={v => setSettings(s => ({ ...s, default_user_limits: { ...s.default_user_limits, max_daily_messages: v } }))} />
                <NumberField label="Emails/Day" tip="Max outreach emails per user per day." value={settings.default_user_limits?.max_daily_emails} onChange={v => setSettings(s => ({ ...s, default_user_limits: { ...s.default_user_limits, max_daily_emails: v } }))} />
                <NumberField label="Scraping Pages/Day" tip="Max LinkedIn pages a user can scrape per day." value={settings.default_user_limits?.max_daily_scraping_pages} onChange={v => setSettings(s => ({ ...s, default_user_limits: { ...s.default_user_limits, max_daily_scraping_pages: v } }))} />
              </div>
            </div>

            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Follower Scraping Limits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Company Followers</h3>
                  <div className="space-y-3">
                    <NumberField label="Max Per Job" tip="Max followers extracted in a single scraping job targeting one company page." value={settings.follower_limits?.company_followers?.max_per_job} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, company_followers: { ...s.follower_limits?.company_followers, max_per_job: v } } }))} />
                    <NumberField label="Max Per Day" tip="Max company followers scraped across ALL jobs combined in 24 hours." value={settings.follower_limits?.company_followers?.max_per_day} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, company_followers: { ...s.follower_limits?.company_followers, max_per_day: v } } }))} />
                    <NumberField label="Cooldown (hours)" tip="Minimum wait before re-scraping the same company page. Prevents detection." value={settings.follower_limits?.company_followers?.cooldown_hours} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, company_followers: { ...s.follower_limits?.company_followers, cooldown_hours: v } } }))} />
                  </div>
                </div>
                <div>
                  <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Personal Followers</h3>
                  <div className="space-y-3">
                    <NumberField label="Max Per Job" tip="Max followers extracted in a single scraping job targeting one personal profile." value={settings.follower_limits?.personal_followers?.max_per_job} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, personal_followers: { ...s.follower_limits?.personal_followers, max_per_job: v } } }))} />
                    <NumberField label="Max Per Day" tip="Max personal followers scraped across ALL jobs combined in 24 hours." value={settings.follower_limits?.personal_followers?.max_per_day} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, personal_followers: { ...s.follower_limits?.personal_followers, max_per_day: v } } }))} />
                    <NumberField label="Cooldown (hours)" value={settings.follower_limits?.personal_followers?.cooldown_hours} onChange={v => setSettings(s => ({ ...s, follower_limits: { ...s.follower_limits, personal_followers: { ...s.follower_limits?.personal_followers, cooldown_hours: v } } }))} />
                  </div>
                </div>
              </div>
            </div>

            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Ramp-Up Configuration <Tip text="Gradual increase in activity limits for new users over 4 weeks. Prevents sudden activity spikes that trigger LinkedIn detection." /></h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <NumberField label="Week 1 (%)" tip="Percentage of full limits allowed in week 1. E.g., 25% of 20 connections = 5/day max." value={settings.ramp_up?.week_1_percentage} onChange={v => setSettings(s => ({ ...s, ramp_up: { ...s.ramp_up, week_1_percentage: v } }))} />
                <NumberField label="Week 2 (%)" tip="Percentage of full limits in week 2." value={settings.ramp_up?.week_2_percentage} onChange={v => setSettings(s => ({ ...s, ramp_up: { ...s.ramp_up, week_2_percentage: v } }))} />
                <NumberField label="Week 3 (%)" tip="Percentage of full limits in week 3." value={settings.ramp_up?.week_3_percentage} onChange={v => setSettings(s => ({ ...s, ramp_up: { ...s.ramp_up, week_3_percentage: v } }))} />
                <NumberField label="Week 4 (%)" tip="Full limits unlocked from week 4 onwards." value={settings.ramp_up?.week_4_percentage} onChange={v => setSettings(s => ({ ...s, ramp_up: { ...s.ramp_up, week_4_percentage: v } }))} />
              </div>
            </div>

            <button onClick={() => saveSettings({ company_limits: settings.company_limits, default_user_limits: settings.default_user_limits, follower_limits: settings.follower_limits, ramp_up: settings.ramp_up })} disabled={saving} data-testid="save-global-settings" className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
            </button>
          </div>
        )}

        {/* LLM Config */}
        {tab === 'llm' && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>LLM Provider Configuration</h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure AI providers for lead scoring. API keys are stored encrypted.</p>

            <div className="mb-4">
              <label className={labelCls}>Active Provider <Tip text="The LLM provider used for lead scoring. Only one provider is active at a time. Configure API keys below." /></label>
              <select value={settings.llm_config?.active_provider || 'openai'} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, active_provider: e.target.value } }))} className={input} data-testid="active-llm-provider">
                <option value="openai">OpenAI (ChatGPT)</option>
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="deepseek">DeepSeek</option>
                <option value="ollama">Ollama (Local)</option>
                <option value="custom">Custom Provider</option>
              </select>
            </div>

            {Object.entries(settings.llm_config?.providers || {}).map(([key, provider]) => (
              <div key={key} className={`mb-4 p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>{key === 'ollama' ? 'Ollama (Local)' : key}</h3>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={provider?.enabled || false} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, providers: { ...s.llm_config.providers, [key]: { ...s.llm_config.providers[key], enabled: e.target.checked } } } }))} className="rounded" />
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Enabled</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {key !== 'ollama' && (
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>API Key</label>
                      <input type="password" value={provider?.api_key || ''} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, providers: { ...s.llm_config.providers, [key]: { ...s.llm_config.providers[key], api_key: e.target.value } } } }))} placeholder="sk-..." className={input} />
                    </div>
                  )}
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Model</label>
                    <input value={provider?.model || ''} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, providers: { ...s.llm_config.providers, [key]: { ...s.llm_config.providers[key], model: e.target.value } } } }))} className={input} />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Endpoint <Tip text="The API URL where scoring requests are sent. E.g. https://api.openai.com/v1/chat/completions for OpenAI. Each provider has a specific URL." /></label>
                    <input value={provider?.endpoint || ''} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, providers: { ...s.llm_config.providers, [key]: { ...s.llm_config.providers[key], endpoint: e.target.value } } } }))} className={input} />
                  </div>
                </div>
              </div>
            ))}

            <div className="mb-4">
              <label className={labelCls}>Min Score Threshold for Auto-Qualify <Tip text="Leads scoring at or above this threshold are automatically moved to 'Qualified' stage. Leads below the disqualify threshold are auto-rejected." /></label>
              <input type="number" min="0" max="100" value={settings.llm_config?.min_score_threshold || 60} onChange={e => setSettings(s => ({ ...s, llm_config: { ...s.llm_config, min_score_threshold: parseInt(e.target.value) || 60 } }))} className={input} />
            </div>

            <button onClick={() => saveSettings({ llm_config: settings.llm_config })} disabled={saving} data-testid="save-llm-settings" className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save LLM Config
            </button>
          </div>
        )}

        {/* Safety */}
        {tab === 'safety' && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Safety Settings</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={settings.safety?.require_active_crm_session} onChange={e => setSettings(s => ({ ...s, safety: { ...s.safety, require_active_crm_session: e.target.checked } }))} className="rounded" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Require active CRM session for automation <Tip text="Automation only runs while the user has an active CRM session. If they log out, all automation pauses." /></span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={settings.safety?.pause_when_user_on_linkedin} onChange={e => setSettings(s => ({ ...s, safety: { ...s.safety, pause_when_user_on_linkedin: e.target.checked } }))} className="rounded" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Pause when user is actively on LinkedIn <Tip text="If the extension detects the user is browsing LinkedIn, all automation pauses instantly to avoid conflicts." /></span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={settings.safety?.emergency_stop_enabled} onChange={e => setSettings(s => ({ ...s, safety: { ...s.safety, emergency_stop_enabled: e.target.checked } }))} className="rounded" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Emergency stop enabled <Tip text="Allows admins to instantly halt all automation across all users with one click." /></span>
              </label>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <NumberField label="Max Consecutive Errors" tip="Number of consecutive failures before auto-pausing a user's automation." value={settings.safety?.max_consecutive_errors} onChange={v => setSettings(s => ({ ...s, safety: { ...s.safety, max_consecutive_errors: v } }))} />
                <NumberField label="Error Cooldown (min)" tip="Minutes to wait after hitting max errors before resuming automation." value={settings.safety?.error_cooldown_minutes} onChange={v => setSettings(s => ({ ...s, safety: { ...s.safety, error_cooldown_minutes: v } }))} />
              </div>
            </div>
            <button onClick={() => saveSettings({ safety: settings.safety })} disabled={saving} data-testid="save-safety-settings" className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50 mt-4">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Safety Settings
            </button>
          </div>
        )}

        {/* User Limits */}
        {tab === 'users' && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>User Management</h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Override default limits for individual users. Blank fields use global defaults.</p>
            <div className="space-y-3">
              {users.crm_users?.map(u => {
                const pu = users.prospecting_users?.find(p => p.user_id === u.user_id);
                return (
                  <div key={u.user_id} className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`} data-testid={`user-${u.user_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</span>
                        <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{u.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${pu?.status === 'active' ? 'bg-green-500/20 text-green-400' : isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                        {pu?.status || 'Not configured'}
                      </span>
                    </div>
                    {pu && (
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                        Overrides: {Object.entries(pu.limit_overrides || {}).filter(([_, v]) => v != null).map(([k, v]) => `${k}: ${v}`).join(', ') || 'None'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Scraping Jobs (embedded) */}
        {tab === 'scraping' && (
          <ScrapingJobsPanel isDark={isDark} card={card} />
        )}
      </div>
    </AppLayout>
  );
}

function ScrapingJobsPanel({ isDark, card }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/scraping/jobs?limit=50`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setJobs(d.jobs); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateJob = async (jobId, action) => {
    await fetch(`${BACKEND_URL}/api/prospecting/scraping/jobs/${jobId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ action }),
    });
    fetchJobs();
  };

  const STATUS_COLORS = { pending: 'bg-yellow-500', processing: 'bg-blue-500', completed: 'bg-green-500', failed: 'bg-red-500', cancelled: 'bg-gray-500' };

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Scraping Jobs</h2>
        <button onClick={fetchJobs} className={`p-2 rounded-lg ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading ? <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#A2FF00] mx-auto"></div></div> : jobs.length === 0 ? (
        <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No scraping jobs</p>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => (
            <div key={job.job_id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{job.payload?.source_url}</p>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{job.payload?.source_type} | max: {job.payload?.max_followers} | {new Date(job.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${STATUS_COLORS[job.status] || 'bg-gray-500'}`}>{job.status}</span>
                {job.status === 'pending' && <button onClick={() => updateJob(job.job_id, 'cancel')} className="text-xs text-red-400 hover:underline">Cancel</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
